// Authentication Service for token and user data management
class AuthService {
  constructor() {
    this.TOKEN_KEY = '_auth_token_';
    this.USER_KEY = '_user_data_';
  }

  /**
   * Store authentication token and user data
   * @param {string} token - Authentication token
   * @param {Object} userData - User data from login response
   */
  setAuthData(token, userData) {
    try {
      sessionStorage.setItem(this.TOKEN_KEY, token);
      sessionStorage.setItem(this.USER_KEY, JSON.stringify(userData));
      return true;
    } catch (error) {
      console.error('Error storing auth data:', error);
      return false;
    }
  }

  /**
   * Get stored authentication token
   * @returns {string|null} - Authentication token or null
   */
  getToken() {
    try {
      return sessionStorage.getItem(this.TOKEN_KEY);
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  }

  /**
   * Get stored user data
   * @returns {Object|null} - User data or null
   */
  getUserData() {
    try {
      const userData = sessionStorage.getItem(this.USER_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error getting user data:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} - True if user has valid token
   */
  isAuthenticated() {
    const token = this.getToken();
    return token !== null && token.length > 0;
  }

  /**
   * Clear authentication data (logout)
   */
  clearAuthData() {
    try {
      sessionStorage.removeItem(this.TOKEN_KEY);
      sessionStorage.removeItem(this.USER_KEY);
      return true;
    } catch (error) {
      console.error('Error clearing auth data:', error);
      return false;
    }
  }

  /**
   * Get user's full name
   * @returns {string} - User's full name or empty string
   */
  getUserFullName() {
    const userData = this.getUserData();
    return userData?.full_name || '';
  }

  /**
   * Get user's student ID
   * @returns {string} - User's student ID or empty string
   */
  getStudentId() {
    const userData = this.getUserData();
    return userData?.student_id || '';
  }

  /**
   * Get user's email
   * @returns {string} - User's email or empty string
   */
  getUserEmail() {
    const userData = this.getUserData();
    return userData?.email || '';
  }

  /**
   * Get user's course information
   * @returns {Object|null} - Course data or null
   */
  getUserCourse() {
    const userData = this.getUserData();
    return userData?.course || null;
  }

  /**
   * Get user's specialization information
   * @returns {Object|null} - Specialization data or null
   */
  getUserSpecialization() {
    const userData = this.getUserData();
    return userData?.specialization || null;
  }

  /**
   * Get authorization header for API requests
   * @returns {Object} - Authorization header object
   */
  getAuthHeader() {
    const token = this.getToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }
}

// Create and export a singleton instance
const authService = new AuthService();
export default authService;
